/**
 * CommandPalette — Operator ⌘K console: grouped commands (ACTIONS/NAVIGATE),
 * record search (RECORDS), and recent picks (RECENTS) behind one flat
 * keyboard cursor. Console-idiom Dialog styled purely from theme.palette.*
 * (no console.css import — this lives in the authenticated shell, unlike
 * the pre-auth trimmed standalone in pages/auth/console).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { ApiService } from '@/utils/api';
import { rankBy } from './fuzzyMatch';
import type { Command } from './commandRegistry';
import type { RecentEntry } from './useCommandRecents';
import { MONO_FONT as MONO } from '@/shared/typography';

interface SearchResult {
  object_name: string;
  object_type: string;
  object_synonym: string;
  record_id: string;
  presentation: string;
}

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  connectionId: string | null;
  commands: Command[];
  recents: RecentEntry[];
  /** `displayLabel` is the human presentation from `_search` (falls back to objectName upstream). */
  onOpenRecord: (objectName: string, recordId: string, displayLabel?: string) => void;
  onRunRecent: (entry: RecentEntry) => void;
  /** Whether the AI assistant entry point is available — gates the ASK AI row. */
  aiEnabled?: boolean;
  /** Invoked with the trimmed query when the ASK AI row is fired. */
  onAskAi?: (query: string) => void;
  /**
   * Whether typing runs the App-mode record search (`/v1/app/{cid}/_search`)
   * and shows the RECORDS group. Default true — existing App-mode callers
   * (MainLayout) are unchanged. The Designer palette has no App record
   * search and passes `false`.
   */
  recordSearch?: boolean;
}

type PaletteItem =
  | { kind: 'command'; key: string; command: Command }
  | { kind: 'record'; key: string; result: SearchResult }
  | { kind: 'recent'; key: string; entry: RecentEntry }
  | { kind: 'ask-ai'; key: string; query: string };

const TOP_ACTIONS_WHEN_EMPTY = 5;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_MIN_CHARS = 2;

const KBD_HINTS: Array<[string, string]> = [
  ['↑↓', 'NAVIGATE'],
  ['↵', 'OPEN'],
  ['esc', 'CLOSE'],
];

export function CommandPalette({
  open,
  onClose,
  connectionId,
  commands,
  recents,
  onOpenRecord,
  onRunRecent,
  aiEnabled = false,
  onAskAi,
  recordSearch = true,
}: CommandPaletteProps) {
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeq = useRef(0);

  // Reset + focus on open; always clear debounce so close mid-flight cannot
  // later fire doSearch(oldTerm) into an empty reopen.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (open) {
      setQuery('');
      setResults([]);
      setCursor(0);
      searchSeq.current += 1; // invalidate in-flight searches from a prior open
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const doSearch = useCallback(
    async (q: string) => {
      const seq = ++searchSeq.current;
      if (!connectionId || q.length < SEARCH_MIN_CHARS) {
        setResults([]);
        return;
      }
      try {
        const resp = await ApiService.get<{ data: SearchResult[] }>(
          `/v1/app/${encodeURIComponent(connectionId)}/_search?q=${encodeURIComponent(q)}&limit=20`,
        );
        if (seq !== searchSeq.current) return;
        setResults(resp.data);
      } catch {
        if (seq !== searchSeq.current) return;
        setResults([]);
      }
    },
    [connectionId],
  );

  const handleChange = (value: string) => {
    setQuery(value);
    setCursor(0);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!recordSearch) {
      setResults([]);
      return;
    }
    // Search the trimmed term so the remote query matches the local ranking
    // (which ranks against query.trim()).
    const term = value.trim();
    if (term.length < SEARCH_MIN_CHARS) {
      // Invalidate any in-flight search for a longer prior term, or its late
      // response would repopulate results after this clear.
      searchSeq.current += 1;
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(() => doSearch(term), SEARCH_DEBOUNCE_MS);
  };

  const trimmedQuery = query.trim();
  const isEmpty = trimmedQuery.length === 0;

  const rankedActions = useMemo(
    () => rankBy(commands.filter((c) => c.group === 'action'), trimmedQuery, (c) => c.label),
    [commands, trimmedQuery],
  );
  const rankedNavigate = useMemo(
    () => rankBy(commands.filter((c) => c.group === 'navigate'), trimmedQuery, (c) => c.label),
    [commands, trimmedQuery],
  );

  const visibleActions = isEmpty ? rankedActions.slice(0, TOP_ACTIONS_WHEN_EMPTY) : rankedActions;
  const visibleNavigate = isEmpty ? [] : rankedNavigate;
  const visibleRecords = isEmpty ? [] : results;
  const visibleRecents = isEmpty ? recents : [];

  const actionItems: PaletteItem[] = visibleActions.map((command) => ({
    kind: 'command',
    key: `cmd:${command.id}`,
    command,
  }));
  const navigateItems: PaletteItem[] = visibleNavigate.map((command) => ({
    kind: 'command',
    key: `cmd:${command.id}`,
    command,
  }));
  const recordItems: PaletteItem[] = visibleRecords.map((result) => ({
    kind: 'record',
    key: `rec:${result.object_name}:${result.record_id}`,
    result,
  }));
  const recentItems: PaletteItem[] = visibleRecents.map((entry) => ({
    kind: 'recent',
    key: `recent:${entry.path}`,
    entry,
  }));

  const showAskAi = aiEnabled && !isEmpty;
  const askAiItems: PaletteItem[] = showAskAi
    ? [{ kind: 'ask-ai', key: 'ask-ai', query: trimmedQuery }]
    : [];

  const items: PaletteItem[] = [...actionItems, ...navigateItems, ...recordItems, ...recentItems, ...askAiItems];
  const effectiveCursor = items.length === 0 ? 0 : Math.min(Math.max(cursor, 0), items.length - 1);

  const actionsStart = 0;
  const navigateStart = actionsStart + actionItems.length;
  const recordsStart = navigateStart + navigateItems.length;
  const recentsStart = recordsStart + recordItems.length;
  const askAiStart = recentsStart + recentItems.length;

  const invoke = (item: PaletteItem) => {
    if (item.kind === 'command') {
      item.command.run();
    } else if (item.kind === 'record') {
      onOpenRecord(
        item.result.object_name,
        item.result.record_id,
        item.result.presentation,
      );
    } else if (item.kind === 'recent') {
      onRunRecent(item.entry);
    } else {
      onAskAi?.(item.query);
    }
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // IME composition (CJK etc.) — don't steal Enter/arrows mid-composition.
    if ((e.nativeEvent as KeyboardEvent).isComposing) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(Math.min(effectiveCursor + 1, Math.max(items.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(Math.max(effectiveCursor - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = items[effectiveCursor];
      if (item) invoke(item);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const groupLabelSx = {
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 1,
    color: theme.palette.text.disabled,
    px: 2,
    pt: 1.5,
    pb: 0.5,
  } as const;

  const renderItem = (item: PaletteItem, index: number) => {
    const selected = index === effectiveCursor;
    const label =
      item.kind === 'command'
        ? item.command.label
        : item.kind === 'record'
          ? item.result.presentation
          : item.kind === 'recent'
            ? item.entry.label
            : `Ask AI: "${item.query}"`;
    const secondary = item.kind === 'record' ? item.result.object_synonym : item.kind === 'command' ? item.command.hint : undefined;
    return (
      <Box
        key={item.key}
        id={`cmd-opt-${index}`}
        role="option"
        aria-selected={selected}
        onMouseEnter={() => setCursor(index)}
        onClick={() => invoke(item)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          px: 2,
          py: 0.875,
          cursor: 'pointer',
          bgcolor: selected ? theme.palette.action.selected : 'transparent',
          '&:hover': { bgcolor: theme.palette.action.hover },
        }}
      >
        <Typography sx={{ fontSize: 13, color: theme.palette.text.primary }} noWrap>
          {label}
        </Typography>
        {secondary && (
          <Typography sx={{ fontSize: 11, fontFamily: MONO, color: theme.palette.text.disabled, whiteSpace: 'nowrap' }}>
            {secondary}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { position: 'fixed', top: '15%', m: 0, borderRadius: 2, maxHeight: '60vh' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box component="span" aria-hidden="true" sx={{ fontFamily: MONO, fontSize: 18, color: theme.palette.text.secondary, mr: 1 }}>
          {'›'}
        </Box>
        <InputBase
          inputRef={inputRef}
          placeholder="Search commands and records…"
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-controls': 'command-palette-listbox',
            'aria-activedescendant':
              items.length > 0 ? `cmd-opt-${effectiveCursor}` : undefined,
          }}
          sx={{ flex: 1, fontSize: 15 }}
          autoFocus
        />
      </Box>

      <Box
        id="command-palette-listbox"
        role="listbox"
        aria-label="Command palette results"
        sx={{ overflowY: 'auto', flex: 1 }}
      >
        {actionItems.length > 0 && (
          <Box>
            <Typography sx={groupLabelSx}>ACTIONS</Typography>
            {actionItems.map((item, i) => renderItem(item, actionsStart + i))}
          </Box>
        )}
        {navigateItems.length > 0 && (
          <Box>
            <Typography sx={groupLabelSx}>NAVIGATE</Typography>
            {navigateItems.map((item, i) => renderItem(item, navigateStart + i))}
          </Box>
        )}
        {recordItems.length > 0 && (
          <Box>
            <Typography sx={groupLabelSx}>RECORDS</Typography>
            {recordItems.map((item, i) => renderItem(item, recordsStart + i))}
          </Box>
        )}
        {recentItems.length > 0 && (
          <Box>
            <Typography sx={groupLabelSx}>RECENTS</Typography>
            {recentItems.map((item, i) => renderItem(item, recentsStart + i))}
          </Box>
        )}
        {askAiItems.length > 0 && (
          <Box>
            <Typography sx={groupLabelSx}>ASK AI</Typography>
            {askAiItems.map((item, i) => renderItem(item, askAiStart + i))}
          </Box>
        )}
        {items.length === 0 && !isEmpty && trimmedQuery.length >= SEARCH_MIN_CHARS && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        {KBD_HINTS.map(([key, label]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box
              component="span"
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                px: 0.5,
                py: 0.125,
                borderRadius: '4px',
                bgcolor: theme.palette.action.selected,
                color: theme.palette.text.secondary,
              }}
            >
              {key}
            </Box>
            <Typography sx={{ fontFamily: MONO, fontSize: 10, letterSpacing: 0.5, color: theme.palette.text.disabled }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Dialog>
  );
}
